import {
  AlertTriangle,
  Check,
  Copy,
  ExternalLink,
  Pencil,
  Plus,
  RefreshCw,
  ShieldAlert,
  ShieldCheck,
  Trash2,
} from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { Link } from "react-router";
import { toast } from "sonner";

import {
  AppRegistrationStatusBadge,
  ENVIRONMENT_LABEL,
  EnvironmentBadge,
  PrivilegedBadge,
  RequestTypeBadge,
  RiskBadge,
} from "@/components/app-registration-badges";
import {
  ConsentConsequences,
  PermissionDiff,
} from "@/components/app-registration-permission-change";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  type AppRegistrationRequestDetail,
  useAppRegistrationScopes,
  useProjectAppRegistrationRequests,
} from "@/lib/api";
import { diffScopeKeys, indexScopes } from "@/lib/app-registration-scopes";
import { formatDate, formatDateTime } from "@/lib/format";
import { logger } from "@/lib/logger";
import { useUser } from "@/lib/user-context";

import { AppRegistrationAmendDialog } from "./app-registration-amend-dialog";
import { AppRegistrationDecommissionDialog } from "./app-registration-decommission-dialog";
import { AppRegistrationRequestDialog } from "./app-registration-request-dialog";
import { SignInConsentNotice } from "./project-sign-in-section";

/** How long the copy button shows its "copied" tick. */
const COPIED_FEEDBACK_MS = 2000;

/** Where an approver works the queue (see `docs/norican/APP_REGISTRATION_REQUESTS.md`). */
const APPROVER_QUEUE_PATH = "/admin/app-registrations";

interface AppRegistrationRequestsSectionProps {
  projectId: number;
  projectName: string;
  /** `canManageResources` — a project owner or an admin. Nobody else sees this. */
  canManage: boolean;
}

/**
 * This project's Entra app registration requests, plus the button to ask for one.
 *
 * Lives in the Resources tab because the tab already answers "what does this
 * project have?" — an app registration is one of those things, and its state
 * (waiting on an approver, live, failed) belongs beside the repo and the
 * resource group rather than in a separate place.
 */
export function AppRegistrationRequestsSection({
  projectId,
  projectName,
  canManage,
}: AppRegistrationRequestsSectionProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [decommissioning, setDecommissioning] = useState<AppRegistrationRequestDetail | null>(null);
  // Only fetch for someone allowed to see it — the backend enforces the same
  // rule, so an unauthorized fetch would only produce a 403 in the console.
  const {
    data: requests,
    isLoading,
    error,
  } = useProjectAppRegistrationRequests(projectId, canManage);

  if (!canManage) {
    return null;
  }

  return (
    <div className="space-y-3 border-t pt-4">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-medium">App registrations</h3>
          <p className="text-muted-foreground text-xs">
            Entra identities this project has asked for, and where each one got to.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={() => setDialogOpen(true)}>
          <Plus className="mr-1 size-3.5" />
          Request an app registration
        </Button>
      </div>

      {isLoading && (
        <p className="text-muted-foreground text-sm">Loading app registration requests…</p>
      )}

      {/* A failed *fetch* is a real error; an empty list is not. */}
      {!isLoading && error && (
        <p className="text-destructive flex items-start gap-2 text-sm">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" />
          <span>Couldn&apos;t load app registration requests. Reopen the tab to retry.</span>
        </p>
      )}

      {!isLoading && !error && requests?.length === 0 && (
        <p className="text-muted-foreground rounded-lg border border-dashed px-4 py-6 text-center text-sm">
          No app registrations requested yet.
        </p>
      )}

      {!isLoading && !error && requests && requests.length > 0 && (
        <div className="space-y-2">
          {requests.map((request) => (
            <RequestRow
              key={request.id}
              request={request}
              siblings={requests}
              // A registration with any change already waiting on a decision
              // must not offer a second: the backend refuses it with a 409,
              // and a button that always errors is worse than no button.
              openChild={findOpenChild(requests, request.id)}
              onDecommission={() => setDecommissioning(request)}
            />
          ))}
        </div>
      )}

      <AppRegistrationRequestDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        projectId={projectId}
        projectName={projectName}
      />

      <AppRegistrationDecommissionDialog
        request={decommissioning}
        projectId={projectId}
        open={decommissioning !== null}
        onOpenChange={(open) => !open && setDecommissioning(null)}
      />
    </div>
  );
}

/** What "still in flight" means for a sibling row — `_OPEN_STATUSES` in migration 0020. */
const OPEN_STATUSES = ["pending", "provisioning", "failed"];

/**
 * The change already in flight against *requestId*, if there is one.
 *
 * A sibling row points back at the original, so the answer is in the list the
 * page already has — no extra request, and no chance of the buttons and the
 * list disagreeing about the same data.
 *
 * One helper for both kinds deliberately. `uq_app_registration_requests_open_
 * child` allows *one* open child of any type, so an open amendment refuses a
 * decommission exactly as another amendment would; asking only about `delete`
 * rows offered a decommission button that could only ever 409.
 */
function findOpenChild(
  requests: AppRegistrationRequestDetail[],
  requestId: string,
): AppRegistrationRequestDetail | undefined {
  return requests.find(
    (row) => row.parent_request_id === requestId && OPEN_STATUSES.includes(row.status),
  );
}

/**
 * Whether this registration can be decommissioned at all.
 *
 * Keyed on `entra_app_id`, not on `status === "provisioned"`, deliberately —
 * and it mirrors the backend's own fence. A provisioning run that failed after
 * creating the application leaves a `failed` row holding a live application,
 * a live enterprise app and a live credential. Those are the rows most worth
 * cleaning up, and a "provisioned only" button would hide the option on
 * exactly them.
 */
function canDecommission(request: AppRegistrationRequestDetail): boolean {
  return (
    request.request_type === "create" &&
    // The project's own sign-in registration is removed with the project, never
    // through the desk: every user signs in with it. The server refuses too.
    request.origin !== "scaffold_sso" &&
    Boolean(request.entra_app_id) &&
    // A run that is still building this registration is not something to hand
    // to a second approver: the Entra ids are committed right after the
    // application is created, well before the service principal and the
    // credential, so the button would otherwise appear mid-provision. The
    // insert refuses it too -- this is the same rule, said on the screen.
    request.status !== "provisioning" &&
    !request.decommissioned_at
  );
}

function RequestRow({
  request,
  siblings,
  openChild,
  onDecommission,
}: {
  request: AppRegistrationRequestDetail;
  /** Every request on this project, so a row can see its own chain. */
  siblings: AppRegistrationRequestDetail[];
  /** The amendment or decommission already in flight against this row, if any. */
  openChild: AppRegistrationRequestDetail | undefined;
  onDecommission: () => void;
}) {
  const { user } = useUser();
  const isAdmin = user?.role === "admin";
  const [amendOpen, setAmendOpen] = useState(false);
  const isAmendment = request.request_type === "amend";
  const isDecommission = request.request_type === "delete";
  // *The* project's own sign-in registration: created by Scaffold UI rather
  // than requested, never decided by an approver, and consented to by IT
  // through the IT approval queue rather than a link on this row. (Its
  // amendments are desk rows, flagged by `sign_in_registration` instead.)
  const isSignIn = request.origin === "scaffold_sso";

  // Only the registration itself offers the button, and only while nothing
  // else is in flight against it. The server refuses either way — this is so
  // a requester does not compose a whole change and then read a 409.
  //
  // `=== "create"` rather than `!isAmendment`: `delete` is already a storable
  // request type, so the negative form would offer "Change permissions" on a
  // decommissioned registration.
  const canAmend =
    request.request_type === "create" &&
    request.status === "provisioned" &&
    !request.decommissioned_at &&
    !findOpenChild(siblings, request.id);

  return (
    <div className="space-y-3 rounded-lg border px-4 py-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-mono text-sm font-medium">
            {request.display_name}
            {isSignIn && (
              <Badge
                variant="secondary"
                className="ml-2 font-sans"
                title="This project signs people in with this app registration"
              >
                Sign-in
              </Badge>
            )}
          </p>
          <p className="text-muted-foreground text-xs">
            {isSignIn ? (
              <>
                Created by Scaffold UI for this project&apos;s sign-in on{" "}
                {formatDate(request.created_at)}
              </>
            ) : (
              <>
                {isAmendment
                  ? "Change requested by"
                  : isDecommission
                    ? "Decommission requested by"
                    : "Requested by"}{" "}
                {request.requester_email} on {formatDate(request.created_at)}
              </>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <RequestTypeBadge requestType={request.request_type} />
          <EnvironmentBadge environment={request.environment} />
          <AppRegistrationStatusBadge
            status={request.status}
            requestType={request.request_type}
            decommissionedAt={request.decommissioned_at}
          />
        </div>
      </div>

      <p className="text-sm">{request.purpose}</p>

      {/* An amendment is a *change*, so it renders as one. Showing only the
          set that won would hide the removals, which are the half this desk
          cannot finish on its own. */}
      {isAmendment && (
        <AmendmentDetails
          request={request}
          signInRegistration={request.sign_in_registration ?? false}
        />
      )}

      {/* A decommission asks for no permissions and federates nothing — both
          of those panels would be empty boilerplate on it. */}
      {request.request_type === "create" && (
        <>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
            <CredentialSummary />
          </div>

          <RequestedScopes scopeKeys={request.requested_scope_keys ?? []} />
        </>
      )}

      {/* Nobody approves the sign-in registration through the desk; its one
          human step is IT's consent, shown in `ProvisionedDetails`. */}
      {!isSignIn && request.approver_email && (
        <p className="text-muted-foreground text-xs">
          {request.status === "rejected" ? "Rejected" : "Approved"} by {request.approver_email}
          {request.decided_at ? ` on ${formatDate(request.decided_at)}` : ""}
          {request.decision_note ? ` — “${request.decision_note}”` : ""}
        </p>
      )}

      {request.status === "provisioned" && request.request_type === "delete" && (
        <DecommissionedDetails request={request} />
      )}

      {/* A retired `create` row keeps `status === "provisioned"` -- that is
          still what happened to the creation -- so branching on the status
          alone kept rendering the green panel, the client id and a consent
          link for an application that no longer exists. */}
      {request.request_type === "create" && request.decommissioned_at && (
        <RetiredDetails request={request} />
      )}

      {/* `ProvisionedDetails` is the creation's success panel — green chrome,
          "Created <date>", and "the app exists and lists the permissions
          above" — and none of that is true of an amendment, least of all one
          that *removed* permissions the tenant is still consented to.
          `AmendmentDetails` above already renders the diff and the consent
          consequences. */}
      {request.status === "provisioned" &&
        request.request_type === "create" &&
        !request.decommissioned_at && <ProvisionedDetails request={request} />}

      {/* A retired creation has nothing left to retry: the queue hides it and
          the approve route refuses it. */}
      {request.status === "failed" && !request.decommissioned_at && (
        <FailureDetails request={request} isAdmin={isAdmin} />
      )}

      {canAmend && (
        <>
          <Button size="sm" variant="outline" onClick={() => setAmendOpen(true)}>
            <Pencil className="mr-1 size-3.5" />
            Change permissions
          </Button>
          <AppRegistrationAmendDialog
            open={amendOpen}
            onOpenChange={setAmendOpen}
            request={request}
          />
        </>
      )}

      {canDecommission(request) &&
        (openChild ? (
          <p className="text-muted-foreground flex items-start gap-1.5 text-xs">
            <Trash2 className="mt-0.5 size-3.5 shrink-0" />
            <span>
              {openChild.request_type === "delete"
                ? "A decommission of this registration is waiting for an approver."
                : "A permission change is waiting for an approver — it has to be decided before this registration can be decommissioned."}
            </span>
          </p>
        ) : (
          <div>
            <Button size="sm" variant="outline" onClick={onDecommission}>
              <Trash2 className="mr-1 size-3.5" />
              Request decommission
            </Button>
          </div>
        ))}
    </div>
  );
}

/**
 * An amendment row: the change it describes, and what it leaves for a human.
 *
 * `base_scope_keys` is on the row, so the diff survives the approval response
 * that first spelled it out — a requester reading this a week later sees what
 * was actually decided rather than only the set that ended up winning.
 */
function AmendmentDetails({
  request,
  signInRegistration,
}: {
  request: AppRegistrationRequestDetail;
  /** It amends the project's own sign-in registration (see `ConsentConsequences`). */
  signInRegistration: boolean;
}) {
  const base = request.base_scope_keys ?? [];
  const requested = request.requested_scope_keys ?? [];
  const diff = diffScopeKeys(base, requested);

  return (
    <div className="space-y-3">
      <PermissionDiff base={base} requested={requested} />
      {/* Only once it has actually been applied. Before that the consequence
          is still hypothetical, and the request row already says it is
          waiting on an approver. */}
      {request.status === "provisioned" && (
        <ConsentConsequences
          added={diff.added}
          removed={diff.removed}
          consentUrl={request.admin_consent_url}
          signInRegistration={signInRegistration}
        />
      )}
    </div>
  );
}

/**
 * The `create` row of a registration that has since been taken away.
 *
 * It keeps its `provisioned` status on purpose -- it still records what
 * happened to the *creation*, which is what an auditor came for -- so this is
 * the only thing that tells a requester skimming their project that the client
 * id above is dead.
 */
function RetiredDetails({ request }: { request: AppRegistrationRequestDetail }) {
  return (
    <div className="space-y-1 rounded-lg border px-3 py-2">
      <p className="text-muted-foreground text-xs">
        Decommissioned {request.decommissioned_at ? formatDateTime(request.decommissioned_at) : ""}
        {request.entra_app_id ? ` — client id ${request.entra_app_id} no longer exists` : ""}.
      </p>
      <p className="text-xs">
        If this project&apos;s deployment still sets{" "}
        <code className="font-mono">GRAPH_CLIENT_ID</code>, remove it and redeploy. Scaffold UI
        cannot change your repository or your deployment.
      </p>
      <p className="text-muted-foreground text-xs">
        This environment is free again — you can request a new registration for it.
      </p>
    </div>
  );
}

/**
 * A registration that has been taken away.
 *
 * The one thing worth saying here is the thing Scaffold UI could not do: the
 * client id is dead, and if the project&apos;s own deployment still carries it
 * as `GRAPH_CLIENT_ID`, every delegated Graph call it makes now fails. Nothing
 * in this app writes or clears that setting.
 */
function DecommissionedDetails({ request }: { request: AppRegistrationRequestDetail }) {
  return (
    <div className="space-y-1 rounded-lg border px-3 py-2">
      {request.application_deleted_at && (
        <p className="text-muted-foreground text-xs">
          Application deleted {formatDateTime(request.application_deleted_at)}
          {request.entra_app_id ? ` — client id ${request.entra_app_id} no longer exists` : ""}.
        </p>
      )}
      <p className="text-xs">
        If this project&apos;s deployment still sets{" "}
        <code className="font-mono">GRAPH_CLIENT_ID</code>, remove it and redeploy. Scaffold UI
        cannot change your repository or your deployment.
      </p>
      <ManualFollowUp items={request.manual_follow_up ?? []} />
    </div>
  );
}

/**
 * What the decommission refused to delete, and a human still has to remove.
 *
 * Refusing to delete an object it cannot prove is ours is the right call --
 * an enterprise application whose own application is gone has no tag left to
 * read -- but that object keeps the delegated permissions a directory admin
 * consented to. Stored on the row rather than only shown on the approver&apos;s
 * result screen, so it survives the page refresh that used to lose it.
 */
function ManualFollowUp({ items }: { items: string[] }) {
  if (items.length === 0) {
    return null;
  }
  return (
    <div className="space-y-1 rounded-md border border-amber-300 bg-amber-50 p-2 dark:border-amber-900 dark:bg-amber-950/40">
      <p className="flex items-center gap-1.5 text-xs font-medium text-amber-900 dark:text-amber-200">
        <AlertTriangle className="size-3.5" />
        Still to remove by hand
      </p>
      {items.map((item) => (
        <p key={item} className="text-xs text-amber-900 dark:text-amber-200">
          {item}
        </p>
      ))}
    </div>
  );
}

function CredentialSummary() {
  // One branch, because there is one credential. The variants this used to
  // switch on -- a client secret, or none at all -- no longer exist: the app
  // registration authenticates as this project's own managed identity, so
  // there is nothing to store and nothing to choose.
  return (
    <>
      <span className="text-muted-foreground inline-flex items-center gap-1">
        <ShieldCheck className="size-3.5" />
        Federated managed identity
      </span>
      <span className="text-muted-foreground">this project&apos;s own app identity</span>
    </>
  );
}

function RequestedScopes({ scopeKeys }: { scopeKeys: string[] }) {
  const { data: scopes } = useAppRegistrationScopes(scopeKeys.length > 0);
  const catalogue = useMemo(() => indexScopes(scopes), [scopes]);

  if (scopeKeys.length === 0) {
    return <p className="text-muted-foreground text-xs">No delegated permissions requested.</p>;
  }

  return (
    <div className="space-y-1.5">
      <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
        Requested permissions
      </p>
      <div className="flex flex-wrap gap-1.5">
        {scopeKeys.map((key) => {
          const scope = catalogue.get(key);
          // Either the catalogue hasn't arrived yet or the server no longer
          // lists this key. Show the raw key rather than dropping the
          // permission — silently hiding one from a reviewer is worse.
          if (!scope) {
            return (
              <Badge key={key} variant="outline" className="font-mono">
                {key}
              </Badge>
            );
          }
          return (
            <span
              key={key}
              className="inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5"
              title={scope.description}
            >
              <span className="font-mono text-xs">{scope.value}</span>
              {scope.privileged && <PrivilegedBadge />}
              <RiskBadge risk={scope.risk} />
              {scope.admin_consent_required && (
                <ShieldAlert
                  className="size-3 text-amber-600 dark:text-amber-400"
                  aria-label="Admin consent required"
                />
              )}
            </span>
          );
        })}
      </div>
    </div>
  );
}

/**
 * What a provisioned request leaves behind: the client id to configure, and
 * the consent link without which the permissions do not actually work.
 *
 * The project's own sign-in registration has no link here: IT consents to it
 * from the IT approval queue, so the row shows where that approval stands.
 */
function ProvisionedDetails({ request }: { request: AppRegistrationRequestDetail }) {
  const [copied, setCopied] = useState(false);
  const copiedTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const copyAppId = async () => {
    if (!request.entra_app_id) {
      return;
    }
    try {
      await navigator.clipboard.writeText(request.entra_app_id);
      setCopied(true);
      clearTimeout(copiedTimeout.current);
      copiedTimeout.current = setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
    } catch (err) {
      logger.error("[app-registration] clipboard write failed:", err);
      toast.error("Couldn't copy the application id.");
    }
  };

  return (
    <div className="space-y-2 rounded-lg border border-green-200 bg-green-50 p-3 dark:border-green-900 dark:bg-green-950/40">
      {request.entra_app_id && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-green-900 dark:text-green-200">
            {/* Which environment this client id belongs to, next to the id
                itself. A project with two registrations has two of these
                panels, and the client id is what a human copies into the
                project's settings by hand -- pasting the dev one into prod is
                the mistake this label exists to prevent. */}
            Application (client) ID
            {request.environment ? ` — ${ENVIRONMENT_LABEL[request.environment]}` : ""}
          </span>
          <code className="font-mono text-xs break-all">{request.entra_app_id}</code>
          <Button size="sm" variant="ghost" onClick={copyAppId}>
            {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
            <span className="sr-only">Copy application id</span>
          </Button>
        </div>
      )}

      {request.provisioned_at && (
        <p className="text-xs text-green-800 dark:text-green-300">
          Created {formatDateTime(request.provisioned_at)}
        </p>
      )}

      {request.origin === "scaffold_sso" ? (
        <SignInConsentNotice
          consentState={request.consent_state}
          consentedAt={request.consented_at}
          pendingText="Waiting for IT approval — sign-in and these permissions work once an IT admin approves it."
        />
      ) : request.admin_consent_url ? (
        <div className="space-y-1">
          <a
            href={request.admin_consent_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm font-medium text-green-800 underline dark:text-green-300"
          >
            Grant admin consent <ExternalLink className="size-3" />
          </a>
          <p className="text-xs text-green-800 dark:text-green-300">
            The app exists and lists the permissions above, but they do not work until a directory
            admin consents.
          </p>
        </div>
      ) : (
        <p className="text-xs text-green-800 dark:text-green-300">
          The consent link wasn&apos;t recorded — ask an approver to retry. The permissions stay
          inactive until a directory admin consents.
        </p>
      )}
    </div>
  );
}

/**
 * A failed provisioning run.
 *
 * The approval is not lost: the backend commits the decision before its first
 * Graph call and stores every object id it created as a resume marker, so
 * approving again picks up where it stopped instead of duplicating anything.
 * That makes "retry" an approver action, which is why this points at the queue
 * rather than offering a button a project owner cannot use.
 */
function FailureDetails({
  request,
  isAdmin,
}: {
  request: AppRegistrationRequestDetail;
  isAdmin: boolean;
}) {
  const isDecommission = request.request_type === "delete";
  return (
    <div className="space-y-2 rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-900 dark:bg-red-950/40">
      <p className="text-sm text-red-800 dark:text-red-200">
        {request.provisioning_error ||
          (isDecommission
            ? "The decommission failed without recording a reason."
            : "Provisioning failed without recording a reason.")}
      </p>
      <p className="flex items-start gap-1.5 text-xs text-red-800 dark:text-red-300">
        <RefreshCw className="mt-0.5 size-3.5 shrink-0" />
        <span>
          {isDecommission
            ? "The approval still stands — retrying resumes the deletion from where it stopped; what is already gone counts as done."
            : "The approval still stands — retrying resumes from where it stopped and creates no duplicates."}
          {!isAdmin && " Ask an approver to retry it."}
        </span>
      </p>
      {isAdmin && (
        <Button size="sm" variant="outline" asChild>
          <Link to={APPROVER_QUEUE_PATH}>
            <RefreshCw className="mr-1 size-3.5" />
            Retry in the approver queue
          </Link>
        </Button>
      )}
    </div>
  );
}
